import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Case Studies API Service (Collection Type - case_study_sections)
export const caseStudiesService = {
  // Get all case study sections data with proper populate
  async getAllCaseStudies(params = {}) {
    try {
      const response = await api.get('/case-study-sections', { 
        params: {
          ...params,
          // Explicitly populate keyResults and other components
          'populate[0]': 'imageHeader.headerImage',
          'populate[1]': 'projectInfo', 
          'populate[2]': 'categories',
          'populate[3]': 'cardImage',
          'populate[4]': 'galleryImages.images',
          'populate[5]': 'Objectives.objectiveItems',
          'populate[6]': 'keyFeatures.featureOptions',
          'populate[7]': 'taskCompleted',
          'populate[8]': 'ChallengesFaced',
          'populate[9]': 'Results',
          'populate[10]': 'keyResults'
        }
      });
      
      return {
        data: response.data.data || [],
        meta: response.data.meta || {},
        success: true
      };
    } catch (error) {
      console.error('❌ Error fetching case study sections:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      return {
        data: [],
        meta: {},
        success: false,
        error: error.message
      };
    }
  },

  // Get case study section by slug or ID
  async getCaseStudyBySlug(slugOrId) {
    try {
      // Try by documentId first (since most IDs will be documentIds)
      try {
        const response = await api.get(`/case-study-sections/${slugOrId}`, {
          params: {
            'populate[0]': 'imageHeader.headerImage',
            'populate[1]': 'projectInfo', 
            'populate[2]': 'categories',
            'populate[3]': 'cardImage',
            'populate[4]': 'galleryImages.images',
            'populate[5]': 'Objectives.objectiveItems',
            'populate[6]': 'keyFeatures.featureOptions',
            'populate[7]': 'taskCompleted',
            'populate[8]': 'ChallengesFaced',
            'populate[9]': 'Results',
            'populate[10]': 'keyResults'
          }
        });
        
        // For single item endpoint, data is in response.data.data (Strapi v5 structure)
        if (response.data && response.data.data) {
          return {
            data: response.data.data,
            success: true
          };
        }
        
        // Fallback for direct data structure (Strapi v4)
        if (response.data && !response.data.data) {
          return {
            data: response.data,
            success: true
          };
        }
      } catch (docIdError) {
        // If documentId fails, try as slug filter (fallback)
        try {
          const response = await api.get('/case-study-sections', {
            params: {
              'filters[slug][$eq]': slugOrId,
              'populate[0]': 'imageHeader.headerImage',
              'populate[1]': 'projectInfo', 
              'populate[2]': 'categories',
              'populate[3]': 'cardImage',
              'populate[4]': 'galleryImages.images',
              'populate[5]': 'Objectives.objectiveItems',
              'populate[6]': 'keyFeatures.featureOptions',
              'populate[7]': 'taskCompleted',
              'populate[8]': 'ChallengesFaced',
              'populate[9]': 'Results',
              'populate[10]': 'keyResults'
            }
          });
          
          if (response.data.data && response.data.data.length > 0) {
            return {
              data: response.data.data[0],
              success: true
            };
          }
        } catch (slugError) {
          console.log('Failed to fetch by slug');
        }
      }
      
      return {
        data: null,
        success: false,
        error: 'Case study section not found'
      };
    } catch (error) {
      console.error('Error fetching case study section:', error);
      return {
        data: null,
        success: false,
        error: error.message
      };
    }
  }
};

// Helper function to format image URLs
export const formatImageUrl = (imageData) => {
  if (!imageData) return null;
  
  const baseUrl = process.env.REACT_APP_API_URL.replace('/api', '');
  
  if (typeof imageData === 'object' && imageData.url) {
    return imageData.url.startsWith('http') ? imageData.url : `${baseUrl}${imageData.url}`;
  }
  
  return null;
};

// Helper function to format case study section data
export const formatCaseStudyData = (caseStudySection) => {
  if (!caseStudySection) return null;


  return {
    ...caseStudySection,
    // Extract title and description from imageHeader if they don't exist at root level
    title: caseStudySection.title || caseStudySection.imageHeader?.title || 'Case Study',
    description: caseStudySection.description || caseStudySection.imageHeader?.description || caseStudySection.projectInfo?.description || '',
    slug: caseStudySection.slug || caseStudySection.documentId, // Fallback to documentId if no slug
    
    // Format imageHeader component (services.services)
    imageHeader: caseStudySection.imageHeader ? {
      ...caseStudySection.imageHeader,
      title: caseStudySection.imageHeader.title || caseStudySection.title,
      description: caseStudySection.imageHeader.description || '',
      headerImage: formatImageUrl(caseStudySection.imageHeader.headerImage),
      backgroundImage: formatImageUrl(caseStudySection.imageHeader.backgroundImage),
      bannerImage: formatImageUrl(caseStudySection.imageHeader.bannerImage),
      image: formatImageUrl(caseStudySection.imageHeader.image)
    } : null,
    
    // Format projectInfo component (mission-vission.mission-vision)
    projectInfo: caseStudySection.projectInfo ? {
      ...caseStudySection.projectInfo,
      title: caseStudySection.projectInfo.title || '',
      description: caseStudySection.projectInfo.description || '',
      missionItems: caseStudySection.projectInfo.missionItems || [],
      visionItems: caseStudySection.projectInfo.visionItems || []
    } : null,
    
    // Format categories component (category.case-study-category)
    categories: caseStudySection.categories?.map(category => ({
      ...category,
      categoryName: category.categoryName || category.name || '',
      categoryDescription: category.categoryDescription || category.description || '',
      categoryImage: formatImageUrl(category.categoryImage || category.image)
    })) || [],
    
    // Format cardImage (single media)
    cardImage: formatImageUrl(caseStudySection.cardImage),
    
    // Format galleryImages component (product.gallery-images) - Updated for multiple images schema
    galleryImages: caseStudySection.galleryImages?.map(galleryItem => ({
      ...galleryItem,
      // Handle both new multiple images schema and old single image schema
      images: galleryItem.images ? (
        Array.isArray(galleryItem.images) 
          ? galleryItem.images.map(img => ({
              ...img,
              url: formatImageUrl(img),
              alt: img?.alternativeText || img?.alt || '',
              caption: img?.caption || ''
            }))
          : [{
              ...galleryItem.images,
              url: formatImageUrl(galleryItem.images),
              alt: galleryItem.images?.alternativeText || galleryItem.images?.alt || '',
              caption: galleryItem.images?.caption || ''
            }]
      ) : [],
      // Keep old single image field for backwards compatibility
      image: formatImageUrl(galleryItem.image),
      alt: galleryItem.alt || galleryItem.alternativeText || '',
      caption: galleryItem.caption || ''
    })) || [],
    
    // Format Objectives component (case-study.objective)
    Objectives: caseStudySection.Objectives ? {
      ...caseStudySection.Objectives,
      title: caseStudySection.Objectives.title || 'Objectives',
      description: caseStudySection.Objectives.description || '',
      objectiveItems: caseStudySection.Objectives.objectiveItems || []
    } : null,
    
    // Format keyFeatures component (services.features)
    keyFeatures: caseStudySection.keyFeatures ? {
      ...caseStudySection.keyFeatures,
      title: caseStudySection.keyFeatures.title || caseStudySection.keyFeatures.featureTitle || 'Key Features',
      description: caseStudySection.keyFeatures.description || '',
      featureOptions: caseStudySection.keyFeatures.featureOptions || []
    } : null,
    
    // Format taskCompleted component array (case-study.task)
    taskCompleted: caseStudySection.taskCompleted?.map(task => ({
      ...task,
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'completed',
      completionDate: task.completionDate || null
    })) || [],
    
    // Format ChallengesFaced component array (case-study.challenge)
    ChallengesFaced: caseStudySection.ChallengesFaced?.map(challenge => ({
      ...challenge,
      title: challenge.title || '',
      description: challenge.description || '',
      solution: challenge.solution || '',
      difficulty: challenge.difficulty || null
    })) || [],
    
    // Format Results component array (case-study.result)
    Results: caseStudySection.Results?.map(result => ({
      ...result,
      title: result.title || '',
      description: result.description || '',
      metric: result.metric || '',
      value: result.value || '',
      percentage: result.percentage || null
    })) || [],
    
    // Format keyResults component array (case-study.key-result)
    keyResults: caseStudySection.keyResults?.map(keyResult => ({
      ...keyResult,
      title: keyResult.title || '',
      description: keyResult.description || '',
      metric: keyResult.metric || '',
      value: keyResult.value || '',
      impact: keyResult.impact || ''
    })) || []
  };
};

// Helper function to extract all gallery images - Updated for multiple images schema
export const extractGalleryImages = (caseStudySection) => {
  if (!caseStudySection?.galleryImages) return [];
  
  return caseStudySection.galleryImages.flatMap(galleryItem => {
    const imageUrls = [];
    
    // Handle new multiple images schema
    if (galleryItem.images && Array.isArray(galleryItem.images)) {
      galleryItem.images.forEach(image => {
        const url = formatImageUrl(image);
        if (url) imageUrls.push(url);
      });
    } else if (galleryItem.images) {
      // Single image object
      const url = formatImageUrl(galleryItem.images);
      if (url) imageUrls.push(url);
    }
    
    // Fallback to old single image field
    if (imageUrls.length === 0 && galleryItem.image) {
      const url = formatImageUrl(galleryItem.image);
      if (url) imageUrls.push(url);
    }
    
    return imageUrls;
  });
};

// Helper function to get case study categories
export const getCaseStudyCategories = (caseStudySection) => {
  if (!caseStudySection?.categories) return [];
  
  return caseStudySection.categories.map(category => ({
    name: category.categoryName,
    description: category.categoryDescription,
    image: formatImageUrl(category.categoryImage)
  }));
};

// Helper function to get formatted objectives
export const getObjectives = (caseStudySection) => {
  if (!caseStudySection?.Objectives) return null;
  
  return {
    title: caseStudySection.Objectives.title,
    description: caseStudySection.Objectives.description,
    objectiveItems: caseStudySection.Objectives.objectiveItems || []
  };
};

// Helper function to get formatted key features
export const getKeyFeatures = (caseStudySection) => {
  if (!caseStudySection?.keyFeatures) return null;
  
  return {
    title: caseStudySection.keyFeatures.featureTitle || caseStudySection.keyFeatures.title || 'Key Features',
    description: caseStudySection.keyFeatures.description || '',
    featureOptions: caseStudySection.keyFeatures.featureOptions || []
  };
};

// Helper function to get project info
export const getProjectInfo = (caseStudySection) => {
  if (!caseStudySection?.projectInfo) return null;
  
  return {
    title: caseStudySection.projectInfo.title,
    description: caseStudySection.projectInfo.description,
    mission: caseStudySection.projectInfo.missionItems || [],
    vision: caseStudySection.projectInfo.visionItems || []
  };
};

// Helper function to get all case study tasks, challenges, and results
export const getCaseStudyProgress = (caseStudySection) => {
  if (!caseStudySection) return null;
  
  return {
    tasks: caseStudySection.taskCompleted || [],
    challenges: caseStudySection.ChallengesFaced || [],
    results: caseStudySection.Results || [],
    keyResults: caseStudySection.keyResults || []
  };
};

export default caseStudiesService;
